import * as DG from "ui/dialogs"
import { ChangeEvent, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { queryCache, useMutation } from "react-query"
import { useHistory, useParams } from "react-router-dom"

import AutoCompleteInput from "components/inputs/AutoComplete"
import Button from "ui/buttons/Button"
import FormControl from "@material-ui/core/FormControl"
import { NewProduct } from "models/common"
import { StartWeekDay } from "models/agency"
import { TextInput } from "components/inputs/TextInput"
import { agencyClientStyles } from "../../AgencyClients/Details/styles"
import { createProducts } from "api/products"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { CircularProgressOverlay } from "ui/utils/CircularProgressOverlay"

export type CreateProductsProps = {
  open: boolean
  onClose: () => void
  options: StartWeekDay[]
}

function CreateProduct(props: CreateProductsProps) {
  const { open, onClose, options } = props
  const history = useHistory()
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const { userRole } = meta ?? {}
  const { id } = useParams<{ id: string }>()

  const initialState = {
    name: "",
    clientId: id,
  }
  const [state, setState] = useState<NewProduct>(initialState)
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  const clearState = () => {
    setState(initialState)
  }

  const onCancel = () => {
    onClose()
    clearState()
  }

  const isAgencyRole =
    userRole === "AGENCY_ADMINISTRATOR" ||
    userRole === "AGENCY_FINANCE" ||
    userRole === "MANAGING_DIRECTOR" ||
    userRole === "PLANNER"

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = evt.target
    !!name && setState((s) => ({ ...s, [name]: value }))
  }

  const handleDropdownChange = (value: StartWeekDay) => {
    setState((s) => ({ ...s, startWeekDay: value }))
  }

  const [create, { status }] = useMutation(createProducts, {
    onSuccess: () => {
      setState(initialState)
      queryCache.removeQueries("client-agency-products")
      onClose()
      !isAgencyRole && history.push(`/market-clients/${id}/products/${id}`)
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleSubmit = () => {
    const pld = {
      clientId: id,
      name: state.name,
      code: state?.code,
      division: state?.division,
      startWeekDay: state?.startWeekDay,
    }
    create(pld).catch((e) => {})
  }

  const dialogId = "add-new-agency-modal"

  return (
    <DG.Dialog
      open={open}
      onClose={onCancel}
      id={dialogId}
      disableEscapeKeyDown
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={dialogId}>
          {t("products.create.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={onCancel} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop>
        <CircularProgressOverlay status={status}>
          <FormControl variant="outlined">
            <div>
              <TextInput
                name="name"
                label={t("products.input.name")}
                value={state?.name}
                margin="normal"
                size="medium"
                className={agencyClientStyles.fieldWidth}
                onChange={handleChange}
                error={!!errors && !!errors["name"]}
                errorMessage={!!errors ? errors["name"] : ""}
              />
            </div>
            <div>
              <TextInput
                name="code"
                value={state?.code}
                label={t("products.input.code")}
                margin="normal"
                size="medium"
                className={agencyClientStyles.codeTextfield}
                onChange={handleChange}
                error={!!errors && !!errors["code"]}
                errorMessage={!!errors ? errors["code"] : ""}
              />
            </div>
            <div>
              <TextInput
                name="division"
                value={state?.division}
                label={t("products.input.division")}
                margin="normal"
                size="medium"
                className={agencyClientStyles.fieldWidth}
                onChange={handleChange}
              />
            </div>
            <div>
              <AutoCompleteInput<StartWeekDay | "">
                id="startWeekDay"
                name="startWeekDay"
                label={t("products.input.start-week-day")}
                size="medium"
                margin="normal"
                fetchOnFilter={false}
                className={agencyClientStyles.fieldWidth}
                options={options || []}
                value={state?.startWeekDay || ""}
                onChange={(val) => {
                  handleDropdownChange(val as StartWeekDay)
                }}
                getOptionSelected={(option, value) => option === value}
                getOptionLabel={(option) =>
                  !!option ? t(`products.input.start-week-day.${option}`) : ""
                }
                renderOption={(option) => (
                  <span>{t(`products.input.start-week-day.${option}`)}</span>
                )}
              />
            </div>
          </FormControl>
        </CircularProgressOverlay>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={onCancel}>
            {t("products.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="new_client"
            onClick={handleSubmit}
            disabled={state.name === ""}
          >
            {t("products.button.submit")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default CreateProduct
